import { memo, useRef, useContext } from 'react';
import { Button, Pagination, Table, Badge, Typography } from 'antd';
import useMarkRecord from './MarkRecord';
import { useSize } from 'ahooks';
import { useCancelMark, EditEstimatedAvailableTime } from './Operate';
import dayjs from 'lib/dayjs';
import { useGetStockRule } from './connect';
import { SearchParamsContext, SelectedRowsContext } from './context';
import EstimatedAvailableTimeTip from 'views/Conversation/Sider/Item/EstimatedAvailableTimeTip';
import { useTranslate } from '@tolgee/react';
import { useGetRegionLabel } from 'hooks/business/useRegion';

const MarkTable = memo(() => {
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const [openMarkRecord, instance] = useMarkRecord();
  const { values: selectedRows, updateSelectedRowKeys } = useContext(SelectedRowsContext);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const height = useSize(tableWrapRef)?.height || 0;
  const cancelMark = useCancelMark();
  const { values, updateSearchParams } = useContext(SearchParamsContext);
  const { data, loading } = useGetStockRule();

  return (
    <div className="flex-grow basis-0 flex flex-col overflow-hidden" ref={tableWrapRef}>
      <div className="flex-grow basis-0">
        {instance}
        <Table
          loading={loading}
          dataSource={data?.stockRuleDataList || []}
          columns={[
            {
              title: t('outofstock_table_spuid'),
              dataIndex: 'spuId',
              key: 'spuId',
              width: 160,
              render: (value) => {
                return (
                  <Typography.Paragraph ellipsis={{ tooltip: true, rows: 2 }}>
                    {value || '-'}
                  </Typography.Paragraph>
                );
              },
            },
            {
              title: t('outofstock_table_skuid'),
              dataIndex: 'skuId',
              key: 'skuId',
              width: 160,
              render: (value) => {
                return (
                  <Typography.Paragraph ellipsis={{ tooltip: true, rows: 2 }}>
                    {value || '-'}
                  </Typography.Paragraph>
                );
              },
            },
            {
              title: t('outofstock_table_sellerid'),
              dataIndex: 'sellerSku',
              key: 'sellerSku',
              width: 160,
              render: (value) => {
                return (
                  <Typography.Paragraph ellipsis={{ tooltip: true, rows: 2 }}>
                    {value || '-'}
                  </Typography.Paragraph>
                );
              },
            },
            {
              title: t('market.平台'),
              dataIndex: 'externalType',
              key: 'externalType',
              width: 110,
              render: (value) => {
                return value || '-';
              },
            },
            {
              title: t('phrase.站点'),
              dataIndex: 'region',
              width: 110,
              render: (value) => {
                return value ? getRegionLabel(value) : '-';
              },
            },
            {
              title: t('outofstock_table_status'),
              key: 'status',
              dataIndex: 'status',
              width: 110,
              render: (value) => {
                return (
                  <div>
                    {value === 1 ? (
                      <Badge status="success" text={t('marketing.生效中')} />
                    ) : (
                      <Badge status="default" text={t('knowledge.已失效')} />
                    )}
                  </div>
                );
              },
            },
            {
              title: (
                <div>
                  <span className="mr-[4px]">{t('outofstock_youhuoriqi')}</span>
                  <EstimatedAvailableTimeTip />
                </div>
              ),
              dataIndex: 'estimatedAvailableTime',
              key: 'estimatedAvailableTime',
              width: 180,
              render: (value, record) => {
                return (
                  <EditEstimatedAvailableTime
                    value={value}
                    id={record.stockRuleId}
                    noEdit={record.status === 2}
                  />
                );
              },
            },
            {
              title: t('outofstock_table_marktime'),
              dataIndex: 'createTimeMs',
              key: 'createTimeMs',
              width: 180,
              render: (value) => {
                return dayjs(Number(value)).format('YYYY-MM-DD HH:mm:ss');
              },
            },
            {
              title: t('操作'),
              key: 'action',
              fixed: 'right',
              width: 240,
              render: (_, record) => {
                return (
                  <div>
                    {record.status === 1 && (
                      <Button
                        onClick={() => {
                          cancelMark([record.stockRuleId]);
                        }}
                        className="!pl-[0]"
                        type="link"
                      >
                        {t('outofstock_action_cancelmark')}
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        openMarkRecord(
                          record.spuId || record.skuId || record.sellerSku,
                          record.stockRuleId,
                        );
                      }}
                      className="!pl-[0]"
                      type="link"
                    >
                      {t('outofstock_action_viewrecord')}
                    </Button>
                  </div>
                );
              },
            },
          ]}
          rowKey="stockRuleId"
          pagination={false}
          {...(values.status === 1
            ? {
                rowSelection: {
                  selectedRowKeys: selectedRows,
                  onChange: (selectedRowKeys) => {
                    updateSelectedRowKeys(selectedRowKeys as string[]);
                  },
                },
              }
            : {})}
          scroll={{
            y: height > 0 ? height - 76 - 28 - 16 - 22 : 0,
          }}
        />
      </div>
      <div className="basis-[76px] pb-[28px] pt-[16px] px-[22px] flex justify-end">
        <Pagination
          pageSize={values.pageSize}
          current={values.pageNo}
          onChange={(page, pageSize) => {
            updateSearchParams({
              pageNo: page,
              pageSize,
            });
          }}
          total={data?.pageInfo?.totalCount || 0}
          showTotal={(x) => t('feedback.共x项', { x })}
          showQuickJumper
        ></Pagination>
      </div>
    </div>
  );
});

export default MarkTable;
